import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { 
  FiLock, FiMail, FiEye, FiEyeOff, FiLoader, 
  FiAlertCircle, FiArrowRight, FiShield, FiUser 
} from 'react-icons/fi';

export default function AdminLogin() {
  const { adminUser, adminLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (adminUser) navigate('/admin');
  }, [adminUser, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email || !code) return setError("Please fill in all fields.");
    if (code.length !== 6) return setError("Code must be 6 digits.");
    
    setLoading(true);
    setStage(1);
    
    try {
      const response = await adminApi.verifyLogin(email, code, remember);
      setStage(2);
      if (response.success && response.user) {
        setStage(3);
        setTimeout(() => setStage(4), 500);
        adminLogin({
          email: response.user.email,
          role: response.user.role,
          admin_session_token: response.admin_session_token,
          session_token: response.session_token,
          expires_at: response.expires_at
        }, remember);
      } else {
        setStage(0);
        setError(response.message || "Invalid credentials.");
      }
    } catch (err) {
      setStage(0);
      setError("Connection failure. Check Gateway URL.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-surface rounded-xl shadow-2xl p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-8 -mr-8 w-32 h-32 bg-primary/5 rounded-full"></div>
        
        <div className="relative z-10 text-center mb-8">
          <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center shadow-xl mx-auto mb-4">
            <SafeIcon icon={FiShield} className="text-accent text-2xl" />
          </div>
          <h1 className="text-xl font-bold text-ink mb-2">Admin Console</h1>
          <p className="text-sm text-muted">BPA Solutions internal access</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-error/5 border border-error/20 rounded-lg flex gap-3 text-error text-sm font-medium">
              <SafeIcon icon={FiAlertCircle} />
              <p>{error}</p>
            </div>
          )}

          {stage > 0 && (
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg flex items-center gap-3">
              <SafeIcon icon={FiLoader} className="text-primary animate-spin" />
              <p className="text-sm font-medium text-primary">
                {stage === 1 && 'Checking credentials...'}
                {stage === 2 && 'Verifying access...'}
                {stage === 3 && 'Creating session...'}
                {stage === 4 && 'Redirecting...'}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Admin Email</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                <SafeIcon icon={FiMail} />
              </div>
              <input 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                placeholder="admin@bpasolutions.au"
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/30 transition-all" 
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Access Code</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                <SafeIcon icon={FiLock} />
              </div>
              <input 
                type={showCode ? "text" : "password"}
                value={code} 
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="••••••"
                className="w-full pl-10 pr-10 py-3 bg-slate-50 border border-border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/30 transition-all tracking-widest font-mono" 
              />
              <button 
                type="button" 
                onClick={() => setShowCode(!showCode)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-primary transition-colors"
              >
                <SafeIcon icon={showCode ? FiEyeOff : FiEye} />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input type="checkbox" id="remember" checked={remember} onChange={() => setRemember(!remember)} className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary/20" />
            <label htmlFor="remember" className="text-sm font-medium text-slate-700 cursor-pointer">Remember this device</label>
          </div>

          <button 
            type="submit" 
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-primary to-accent text-white rounded-lg font-medium flex items-center justify-center gap-2 shadow-card hover:-translate-y-0.5 transition-premium active:scale-95 disabled:opacity-50"
          >
            {loading ? (
              <><SafeIcon icon={FiLoader} className="animate-spin" /> Authorizing...</>
            ) : (
              <>Authorize Access <SafeIcon icon={FiArrowRight} /></>
            )}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-border flex justify-center">
          <Link to="/login" className="flex items-center gap-2 text-sm font-medium text-muted hover:text-primary transition-colors">
            <SafeIcon icon={FiUser} />
            <span>Client portal? <span className="underline">Login here</span></span>
          </Link>
        </div>
      </div>
    </div>
  );
}